import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="arrivals-filter" -- reused on both the
// Upcoming Arrivals and Upcoming Departures dashboard timeline cards.
//
// Rows are pre-rendered server-side with data-status="pending"|"done".
// Checking a guest in/out is a real PATCH to the reservation controller;
// we don't re-fetch the list afterward (that controller isn't ours to
// change here), so on a successful submit we flip the row's own state
// client-side instead -- an optimistic update, not a fake one, since the
// PATCH already round-tripped and succeeded before we touch the DOM.
export default class extends Controller {
  static targets = ["input", "segment", "row", "list", "emptyState", "progressBar", "progressLabel"]
  static values = {
    segment: { type: String, default: "pending" },
    progressNoun: { type: String, default: "checked in" }
  }

  connect() {
    this.applySegmentStyles()
    this.applyFilters()
  }

  filter() {
    this.applyFilters()
  }

  setSegment(event) {
    this.segmentValue = event.currentTarget.dataset.segment
    this.applySegmentStyles()
    this.applyFilters()
  }

  handleActionSuccess(event) {
    if (!event.detail || !event.detail.success) return

    const row = event.target.closest("[data-status]")
    if (!row || row.dataset.status === "done") return

    row.dataset.status = "done"
    const pendingAction = row.querySelector('[data-role="pending-action"]')
    const doneAction = row.querySelector('[data-role="done-action"]')
    if (pendingAction) pendingAction.classList.add("hidden")
    if (doneAction) doneAction.classList.remove("hidden")

    this.applyFilters()
  }

  applySegmentStyles() {
    this.segmentTargets.forEach(button => {
      const active = button.dataset.segment === this.segmentValue
      button.classList.toggle("bg-white", active)
      button.classList.toggle("dark:bg-stone-700", active)
      button.classList.toggle("text-primary-700", active)
      button.classList.toggle("dark:text-primary-300", active)
      button.classList.toggle("shadow-sm", active)
      button.classList.toggle("text-stone-500", !active)
      button.classList.toggle("dark:text-stone-400", !active)
    })
  }

  applyFilters() {
    const term = (this.hasInputTarget ? this.inputTarget.value : "").trim().toLowerCase()
    let visibleCount = 0

    this.rowTargets.forEach(row => {
      const matchesSegment = this.segmentValue === "all" || row.dataset.status === this.segmentValue
      const matchesTerm = !term || row.dataset.name.includes(term) || row.dataset.site.includes(term)
      const visible = matchesSegment && matchesTerm
      row.classList.toggle("hidden", !visible)
      if (visible) visibleCount += 1
    })

    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.toggle("hidden", visibleCount > 0)
    }

    this.updateProgress()
  }

  updateProgress() {
    const total = this.rowTargets.length
    const done = this.rowTargets.filter(row => row.dataset.status === "done").length

    if (this.hasProgressBarTarget) {
      const pct = total === 0 ? 0 : Math.round((done / total) * 100)
      this.progressBarTarget.style.width = `${pct}%`
    }

    if (this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent = `${done} of ${total} ${this.progressNounValue}`
    }
  }
}
